import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db73ptun6nb9f68e9900";

const teamMembers = [
  { name: "赛事运营组", desc: "负责每日赛程整理、比分更新与赛事专题策划，确保信息及时准确。", icon: "ri-calendar-check-line" },
  { name: "数据分析组", desc: "负责球队与球员数据的整理与可视化展示，输出深度数据洞察。", icon: "ri-bar-chart-box-line" },
  { name: "产品体验组", desc: "负责网站交互与视觉体验优化，持续打磨更易用的观赛入口。", icon: "ri-layout-4-line" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "关于我们 - NBA直播吧",
    description: "了解 NBA直播吧 的网站定位、运营团队与联系方式。我们专注为篮球爱好者提供权威、及时的 NBA 赛事直播入口、实时比分与赛程资讯。",
    keywords: "NBA直播吧,关于我们,NBA赛事直播平台,篮球资讯门户",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      const text = typeof value === "string" ? value : "";
      if (key === "website_alt") return;
      if (!text.trim()) return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(isSpam ? "提交内容被判定为异常，请稍后重试。" : serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div>
      <div className="bg-primary-800">
        <div className="mx-auto max-w-page px-4 py-14 md:px-6 md:py-20">
          <nav className="mb-5 flex items-center gap-2 text-sm text-background-100/70">
            <Link to="/" className="hover:text-accent-300 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">关于 NBA直播吧</h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-background-100/85">
            NBA直播吧 是面向国内外篮球爱好者的 NBA 赛事信息门户，围绕 NBA 官方赛事提供直播入口、实时比分、赛程浏览与回放查询服务。
            我们希望用清晰、专业、及时的信息呈现，让每一位球迷都能快速找到自己想看的比赛。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        {/* 定位与使命 */}
        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-focus-3-line text-xl" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">网站定位</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              以实时直播与比分信息为核心的现代门户体育网站，强调赛事的权威展示与实时互动性，兼顾数据深度与简洁易用。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-flag-2-line text-xl" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">我们的使命</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              让篮球迷第一时间掌握 NBA 赛程、比分与赛况，用专业的数据与清晰的指引，陪伴每一场值得期待的比赛。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
              <i className="ri-user-heart-line text-xl" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">服务人群</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              面向喜欢观看 NBA 常规赛、季后赛及全明星赛的国内外篮球爱好者，覆盖观赛、查比分、看回放等核心需求。
            </p>
          </div>
        </section>

        {/* 团队介绍 */}
        <section className="mt-12">
          <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">团队介绍</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {teamMembers.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 text-foreground-700">
                  <i className={`${member.icon} text-lg`} />
                </span>
                <h3 className="font-heading text-base font-semibold text-foreground-950">{member.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 联系方式 + 反馈表单 */}
        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">联系方式</h2>
            <div className="space-y-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">反馈邮箱</p>
                  <p className="text-sm text-foreground-500">feedback@nba-livebar.example</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">服务时间</p>
                  <p className="text-sm text-foreground-500">每日 09:00 - 24:00（比赛日延长更新）</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                  <i className="ri-map-pin-line" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">运营中心</p>
                  <p className="text-sm text-foreground-500">线上运营 · 面向全国球迷</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="mb-5 font-heading text-xl md:text-2xl font-bold text-foreground-950">意见反馈</h2>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700" htmlFor="feedback-name">你的称呼</label>
                  <input
                    id="feedback-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground-700" htmlFor="feedback-email">邮箱</label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium text-foreground-700" htmlFor="feedback-subject">反馈类型</label>
                <select
                  id="feedback-subject"
                  name="subject"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="功能建议">功能建议</option>
                  <option value="赛事信息纠错">赛事信息纠错</option>
                  <option value="内容合作">内容合作</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium text-foreground-700" htmlFor="feedback-message">反馈内容</label>
                <textarea
                  id="feedback-message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  placeholder="请描述你的建议或问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-field-invisible"
              />

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line" />
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-1.5 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line" />
                    感谢你的反馈，我们已经收到！
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-1.5 text-sm text-accent-700">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>

        {/* 版权与免责声明 */}
        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">版权声明与免责声明</h2>
          <div className="mt-3 space-y-2 text-sm leading-relaxed text-foreground-600">
            <p>本站为前端演示项目，站内赛事比分、赛况动态、球员数据及视频区域均为示例内容，未接入真实数据接口与流媒体资源。</p>
            <p>本站与 NBA 官方及其关联机构无任何从属关系，文中所涉及的球队名称、商标与标识归各自所有者所有，仅用于信息展示。</p>
            <p>© 2026 NBA直播吧 · 保留所有权利。</p>
          </div>
        </section>
      </div>
    </div>
  );
}